import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { equiv } from '@thi.ng/equiv';
import { CrudItemComponent, onValueChangeEvent, Privilege, PrivilegeEntityDecorator, PrivilegeService, PRIVILEGE_METADATA, PRIVILEGE_TYPE, requiredValidator, Role, RoleEntityDecorator, RoleService, ROLE_METADATA, ROLE_TYPE, UserStorage } from '@scisys-uim/uim-common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AssetsUserStorage } from 'assets-service';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { CrudEditorParentService, CrudEditorSelfService } from '@scisys-uim/uim-common';

const roleNamePattern: RegExp = new RegExp("^[A-Z][A-Z0-9_]*$");

@Component({
  selector: 'app-role-item',
  templateUrl: './role-item.component.html',
  styleUrls: ['./role-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class RoleItemComponent extends CrudItemComponent<Role, string> {

  protected systemConfig: any;

  public form: FormGroup;

  allRoles: Role[];
  @ViewChild('nestedRoles') nestedRolesInput: ElementRef;
  private nestedRolesSelectionListSubject: Subject<Role[]> = new BehaviorSubject([]);
  nestedRolesSelectionList: Observable<Role[]> = this.nestedRolesSelectionListSubject.asObservable();

  allPrivileges: Privilege[];
  @ViewChild('ownPrivileges') ownPrivilegesInput: ElementRef;
  private ownPrivilegesSelectionListSubject: Subject<Privilege[]> = new BehaviorSubject([]);
  ownPrivilegesSelectionList: Observable<Privilege[]> = this.ownPrivilegesSelectionListSubject.asObservable();


  constructor(protected roleService: RoleService,
    protected privilegeService: PrivilegeService,
    protected userDetailsStorage: AssetsUserStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(ROLE_TYPE, fb, elementRef, router, route, userStorage, dialog, roleService, injector, snackbar);
  }

  createForm(): FormGroup {
    return this.fb.group({
      name: [null, [requiredValidator, (control) => {
        let regex: RegExp = new RegExp("^[A-Z0-9_]$");
        return (roleNamePattern.test(control.value)) ? null : { roleNameFormat: "Must contain only alphanumeric characters and underscore" };
      }]],
      description: [null, [requiredValidator]],
      systemProvided: [null],
      ownPrivileges: [null],
      nestedRoles: [null]
    });
  }


  ngAfterViewInit(): void {
    if (this.ownPrivileges) {
      onValueChangeEvent(this.ownPrivileges, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.ownPrivileges = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.ownPrivilegesSelectionListFilter(value);
        }
      });
    }
    if (this.nestedRoles) {
      onValueChangeEvent(this.nestedRoles, value => {
        if (value == "") {
          value = null;
          if (this.item) {
            this.item.nestedRoles = null;
          }
        }
        if (value == null || typeof value == 'string') {
          this.nestedRolesSelectionListFilter(value);
        }
      });
    }
  }

  getChangedSingleItemEquiv(): (a, b) => boolean {
    return (a, b) => {
      if (a.name && b.name) {
        return a.name == b.name;
      }
      return equiv(a, b);
    };
  }

  ngOnInit() {
    super.ngOnInit();

    function initOptions(config: any): any {
      config = config || {};
      return config;
    }
    this.systemConfig = initOptions({});
    this.configStorage.itemChanges().subscribe(config => {
      this.systemConfig = initOptions(config);
    });

    this.ownPrivilegesLoadSelectionList();
    this.nestedRolesLoadSelectionList();
  }

  itemSet() {
    super.itemSet();
    this.nestedRolesSelectionListFilter(null);
    this.ownPrivilegesSelectionListFilter(null);
  }

  setDefaults() {
    if (!this.readOnly && this.createMode) {
      this.item.systemProvided = false;
      this.item.nestedRoles = this.item.nestedRoles || [];
      this.item.ownPrivileges = this.item.ownPrivileges || [];

      this.itemSet();
    }
    this.setDefaultDisabledFields();
  }

  setDefaultDisabledFields() {
    if (!this.form) {
      return;
    }
    this.systemProvided.disable();
    if (!this.createMode) {
      this.name.disable();
    }
  }

  allowsEditItem() {
    if (super.allowsEditItem()) {
      return this.item && !this.item.systemProvided;
    }
    return false;
  }

  systemProvidedChanging(value: boolean, unsetDependents: boolean = true) { }

  systemProvidedChanged(value: boolean, unsetDependents: boolean = true) { }

  descriptionChanging(value: string, unsetDependents: boolean = true) { }

  descriptionChanged(value: string, unsetDependents: boolean = true) { }

  ownPrivilegesDisplay(item: Privilege) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return PRIVILEGE_TYPE.description(item);
  }

  selectedOwnPrivilege(event: MatAutocompleteSelectedEvent): void {
    var newItem: Privilege = this.allPrivileges.find(p => p.name === event.option.viewValue);
    if (this.item.ownPrivileges) {
      this.item.ownPrivileges.push(newItem);
    } else {
      this.item.ownPrivileges = [newItem];
    }
    this.ownPrivilegesInput.nativeElement.value = '';
    this.ownPrivileges.setValue(null);
    this.ownPrivilegesSelectionListFilter(null);
  }


  ownPrivilegesLoadSelectionList() {
    this.privilegeService.findAll().subscribe(
      data => {
        this.allPrivileges = data.items;
        this.ownPrivilegesSelectionListFilter(null);
      }
    );
  }

  ownPrivilegesSelectionListFilter(text: string): void {
    var filtered = this.allPrivileges ? this.allPrivileges.filter(p => !(this.item.ownPrivileges || []).find(o => o.name == p.name)) : [];
    if (text != null && text.length > 0) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(text.toLowerCase()));
    }
    this.ownPrivilegesSelectionListSubject.next(filtered);
  }

  ownPrivilegesChanging(value: Privilege[], unsetDependents: boolean = true) { }

  ownPrivilegesChanged(value: Privilege[], unsetDependents: boolean = true) {
  }

  ownPrivilegesCompare(a: any, b: any) {
    return PRIVILEGE_METADATA.equals(a, b);
  }

  removeOwnPrivilege(privilege: Privilege): void {
    const index = this.item.ownPrivileges.indexOf(privilege);

    if (index >= 0) {
      this.item.ownPrivileges.splice(index, 1);
      this.ownPrivilegesSelectionListFilter(null);
    }
  }

  nestedRolesChanging(value: Role[], unsetDependents: boolean = true) { }

  nestedRolesChanged(value: Role[], unsetDependents: boolean = true) { }

  nestedRolesDisplay(item: Role) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return ROLE_TYPE.description(item);
  }

  selectedNestedRole(event: MatAutocompleteSelectedEvent): void {
    var newItem: Role = this.allRoles.find(p => p.name === event.option.viewValue);
    if (this.item.nestedRoles) {
      this.item.nestedRoles.push(newItem);
    } else {
      this.item.nestedRoles = [newItem];
    }
    this.nestedRolesInput.nativeElement.value = '';
    this.nestedRoles.setValue(null);
    this.nestedRolesSelectionListFilter(null);
  }

  nestedRolesLoadSelectionList() {
    this.roleService.findAll().subscribe(
      data => {
        this.allRoles = data.items;
        this.nestedRolesSelectionListFilter(null);
      }
    );
  }

  nestedRolesSelectionListFilter(text: string): void {
    var filtered = this.allRoles ? this.allRoles.filter(p =>
      p.name != this.item.name && !(this.item.nestedRoles || []).find(o => o.name == p.name)) : [];
    if (text != null && text.length > 0) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(text.toLowerCase()));
    }
    this.nestedRolesSelectionListSubject.next(filtered);
  }

  nestedRolesCompare(a: any, b: any) {
    return ROLE_METADATA.equals(a, b);
  }

  removeNestedRole(role: Role): void {
    const index = this.item.nestedRoles.indexOf(role);

    if (index >= 0) {
      this.item.nestedRoles.splice(index, 1);
      this.nestedRolesSelectionListFilter(null);
    }
  }

  nameChanging(value: string, unsetDependents: boolean = true) { }

  nameChanged(value: string, unsetDependents: boolean = true) { }


  control(name: string) {
    return this.form ? this.form.get(name) : null;
  }

  get name() {
    return this.control('name');
  }

  get description() {
    return this.control('description');
  }

  get ownPrivileges() {
    return this.control('ownPrivileges');
  }

  get nestedRoles() {
    return this.control('nestedRoles');
  }

  get systemProvided() {
    return this.control('systemProvided');
  }
}





